import React, { useMemo, useState } from 'react';
import { TRANSACTIONS, ACCOUNTS, FAILURE_REASON_LABELS, type Transaction } from '../../data/mockData';
import { Download, X, MessageSquare, Smartphone, Hash } from 'lucide-react';
type StatusFilter = 'all' | 'settled' | 'pending' | 'processing' | 'failed';
type ChannelFilter = 'all' | 'USSD' | 'WhatsApp' | 'SMS';
type SortKey = 'time' | 'amount' | 'response';
const STATUS_OPTIONS: {
  value: StatusFilter;
  label: string;
  count?: number;
}[] = [{
  value: 'all',
  label: 'All'
}, {
  value: 'settled',
  label: 'Settled'
}, {
  value: 'pending',
  label: 'Pending'
}, {
  value: 'processing',
  label: 'Processing'
}, {
  value: 'failed',
  label: 'Failed'
}];
const CHANNEL_OPTIONS: {
  value: ChannelFilter;
  label: string;
  icon?: React.ReactNode;
}[] = [{
  value: 'all',
  label: 'All channels'
}, {
  value: 'USSD',
  label: 'USSD',
  icon: <Hash className="w-3.5 h-3.5" data-id="element-898" />
}, {
  value: 'WhatsApp',
  label: 'WhatsApp',
  icon: <MessageSquare className="w-3.5 h-3.5" data-id="element-899" />
}, {
  value: 'SMS',
  label: 'SMS',
  icon: <Smartphone className="w-3.5 h-3.5" data-id="element-900" />
}];
function formatResponseTime(ms: number, isTimeout: boolean) {
  if (isTimeout) return 'Timed out';
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
}
function getResponseTimeColor(ms: number, isFailure: boolean) {
  if (isFailure) return 'text-red-400';
  if (ms < 2000) return 'text-mint';
  if (ms < 4000) return 'text-yellow-500';
  return 'text-red-400';
}
export function Transactions() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [channelFilter, setChannelFilter] = useState<ChannelFilter>('all');
  const [showTimeoutsOnly, setShowTimeoutsOnly] = useState(false);
  const [sortKey, setSortKey] = useState<SortKey>('time');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // Count by status for filter pills
  const statusCounts = useMemo(() => {
    const counts: Record<StatusFilter, number> = {
      all: TRANSACTIONS.length,
      settled: 0,
      pending: 0,
      processing: 0,
      failed: 0
    };
    TRANSACTIONS.forEach(tx => {
      counts[tx.status as StatusFilter]++;
    });
    return counts;
  }, []);
  const timeoutCount = useMemo(() => TRANSACTIONS.filter(tx => tx.failureReason === 'timeout').length, []);
  // Apply filters + sort
  const filteredTx = useMemo(() => {
    let rows: Transaction[] = TRANSACTIONS.filter(tx => {
      if (statusFilter !== 'all' && tx.status !== statusFilter) return false;
      if (channelFilter !== 'all' && tx.channel !== channelFilter) return false;
      if (showTimeoutsOnly && tx.failureReason !== 'timeout') return false;
      return true;
    });
    rows = [...rows].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'time') cmp = new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime();else if (sortKey === 'amount') cmp = a.amount - b.amount;else if (sortKey === 'response') cmp = a.responseTimeMs - b.responseTimeMs;
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [statusFilter, channelFilter, showTimeoutsOnly, sortKey, sortDir]);
  const activeFilterCount = (statusFilter !== 'all' ? 1 : 0) + (channelFilter !== 'all' ? 1 : 0) + (showTimeoutsOnly ? 1 : 0);
  const clearFilters = () => {
    setStatusFilter('all');
    setChannelFilter('all');
    setShowTimeoutsOnly(false);
  };
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');else {
      setSortKey(key);
      setSortDir('desc');
    }
  };
  return <div className="max-w-6xl mx-auto space-y-6" data-id="element-901">
      <div className="flex items-center justify-between" data-id="element-902">
        <div data-id="element-903">
          <h1 className="text-2xl font-semibold text-bone" data-id="element-904">Transactions</h1>
          <p className="text-sm text-muted mt-1" data-id="element-905">
            {filteredTx.length} of {TRANSACTIONS.length} transactions
            {activeFilterCount > 0 && ` · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'} applied`}
          </p>
        </div>
        <button className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-ink-3 border border-white/10 text-sm font-medium text-bone hover:bg-ink-4 transition-colors" data-id="element-906">
          <Download className="w-4 h-4" data-id="element-907" />
          Export CSV
        </button>
      </div>

      {/* Filter bar */}
      <div className="bg-ink-2 border border-white/5 rounded-2xl p-4 space-y-4" data-id="element-908">
        {/* Status pills */}
        <div data-id="element-909">
          <div className="text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-910">
            Status
          </div>
          <div className="flex flex-wrap gap-2" data-id="element-911">
            {STATUS_OPTIONS.map(opt => {
            const isActive = statusFilter === opt.value;
            return <button key={opt.value} onClick={() => setStatusFilter(opt.value)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${isActive ? 'bg-mint text-ink border-mint' : 'bg-ink border-white/10 text-bone-dim hover:text-bone hover:border-white/20'}`} data-id="element-912">
                  
                  {opt.label}
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${isActive ? 'bg-ink/20 text-ink' : 'bg-ink-3 text-muted'}`} data-id="element-913">
                    
                    {statusCounts[opt.value]}
                  </span>
                </button>;
          })}
          </div>
        </div>

        {/* Channel + timeout toggles */}
        <div className="flex flex-wrap items-end gap-6 pt-2 border-t border-white/5" data-id="element-914">
          <div data-id="element-915">
            <div className="text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-916">
              Channel
            </div>
            <div className="flex flex-wrap gap-2" data-id="element-917">
              {CHANNEL_OPTIONS.map(opt => {
              const isActive = channelFilter === opt.value;
              return <button key={opt.value} onClick={() => setChannelFilter(opt.value)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${isActive ? 'bg-mint text-ink border-mint' : 'bg-ink border-white/10 text-bone-dim hover:text-bone hover:border-white/20'}`} data-id="element-918">
                    
                    {opt.icon}
                    {opt.label}
                  </button>;
            })}
            </div>
          </div>

          <div data-id="element-919">
            <div className="text-xs font-medium text-muted uppercase tracking-wider mb-2" data-id="element-920">
              Quick filter
            </div>
            <button onClick={() => setShowTimeoutsOnly(!showTimeoutsOnly)} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors border ${showTimeoutsOnly ? 'bg-red-500/20 text-red-300 border-red-500/40' : 'bg-ink border-white/10 text-bone-dim hover:text-bone hover:border-white/20'}`} data-id="element-921">
              
              Timeouts only
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${showTimeoutsOnly ? 'bg-red-500/30 text-red-200' : 'bg-ink-3 text-muted'}`} data-id="element-922">
                
                {timeoutCount}
              </span>
            </button>
          </div>

          {activeFilterCount > 0 && <button onClick={clearFilters} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium text-muted hover:text-bone transition-colors ml-auto" data-id="element-923">
            
              <X className="w-3.5 h-3.5" data-id="element-924" />
              Clear all
            </button>}
        </div>
      </div>

      {/* Table */}
      <div className="bg-ink-2 border border-white/5 rounded-2xl overflow-hidden" data-id="element-925">
        <div className="overflow-x-auto" data-id="element-926">
          <table className="w-full text-left text-sm" data-id="element-927">
            <thead className="bg-ink text-muted uppercase tracking-wider text-xs" data-id="element-928">
              <tr data-id="element-929">
                <th className="px-6 py-4 font-medium" data-id="element-930">Reference</th>
                <th className="px-6 py-4 font-medium" data-id="element-931">Customer</th>
                <th className="px-6 py-4 font-medium" data-id="element-932">
                  <button onClick={() => toggleSort('amount')} className="inline-flex items-center gap-1 hover:text-bone transition-colors" data-id="element-933">
                    
                    Amount{' '}
                    {sortKey === 'amount' && <span className="text-mint" data-id="element-934">
                        {sortDir === 'desc' ? '↓' : '↑'}
                      </span>}
                  </button>
                </th>
                <th className="px-6 py-4 font-medium" data-id="element-935">Channel</th>
                <th className="px-6 py-4 font-medium" data-id="element-936">
                  <button onClick={() => toggleSort('response')} className="inline-flex items-center gap-1 hover:text-bone transition-colors" data-id="element-937">
                    
                    Response time{' '}
                    {sortKey === 'response' && <span className="text-mint" data-id="element-938">
                        {sortDir === 'desc' ? '↓' : '↑'}
                      </span>}
                  </button>
                </th>
                <th className="px-6 py-4 font-medium" data-id="element-939">Status</th>
                <th className="px-6 py-4 font-medium" data-id="element-940">
                  <button onClick={() => toggleSort('time')} className="inline-flex items-center gap-1 hover:text-bone transition-colors" data-id="element-941">
                    
                    Time{' '}
                    {sortKey === 'time' && <span className="text-mint" data-id="element-942">
                        {sortDir === 'desc' ? '↓' : '↑'}
                      </span>}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5" data-id="element-943">
              {filteredTx.map(tx => {
              const account = ACCOUNTS.find(a => a.id === tx.accountId);
              const isTimeout = tx.failureReason === 'timeout';
              const isFailure = tx.status === 'failed';
              return <tr key={tx.id} className="hover:bg-ink-3/50 transition-colors" data-id="element-944">
                    
                    <td className="px-6 py-4 mono text-bone" data-id="element-945">{tx.ref}</td>
                    <td className="px-6 py-4" data-id="element-946">
                      <div className="font-medium text-bone" data-id="element-947">
                        {account?.name || 'Unknown'}
                      </div>
                      <div className="text-muted mono text-xs mt-0.5" data-id="element-948">
                        {account?.ref}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-medium text-bone" data-id="element-949">
                      R {tx.amount.toFixed(2)}
                    </td>
                    <td className="px-6 py-4" data-id="element-950">
                      <span className="inline-flex items-center px-2 py-1 rounded bg-ink-4 text-bone-dim text-xs mono" data-id="element-951">
                        {tx.channel}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-952">
                      <span className={`mono text-sm font-medium ${getResponseTimeColor(tx.responseTimeMs, isFailure)}`} data-id="element-953">
                        
                        {formatResponseTime(tx.responseTimeMs, isTimeout)}
                      </span>
                    </td>
                    <td className="px-6 py-4" data-id="element-954">
                      <div className="flex flex-col gap-1" data-id="element-955">
                        <span className={`inline-flex items-center self-start px-2.5 py-1 rounded-full text-xs font-medium ${tx.status === 'settled' ? 'bg-mint/10 text-mint' : tx.status === 'pending' ? 'bg-yellow-500/10 text-yellow-500' : tx.status === 'processing' ? 'bg-blue-500/10 text-blue-400' : 'bg-red-500/10 text-red-400'}`} data-id="element-956">
                          
                          {tx.status.charAt(0).toUpperCase() + tx.status.slice(1)}
                        </span>
                        {tx.failureReason && <span className="text-[11px] text-muted" data-id="element-957">
                            {FAILURE_REASON_LABELS[tx.failureReason]}
                          </span>}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-muted" data-id="element-958">
                      {new Date(tx.timestamp).toLocaleString([], {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                    </td>
                  </tr>;
            })}
            </tbody>
          </table>
        </div>

        {filteredTx.length === 0 && <div className="p-12 text-center" data-id="element-959">
            <div className="text-bone-dim mb-2" data-id="element-960">
              No transactions match the current filters.
            </div>
            <button onClick={clearFilters} className="text-mint hover:text-mint-dim text-sm font-medium" data-id="element-961">
            
              Clear filters
            </button>
          </div>}
      </div>
    </div>;
}